const FOCUS_ATTRIBUTES = ['data-quantity-plus', 'data-quantity-minus'];
const SELECTABLE_INPUT_TYPES = ['text', 'search', 'tel', 'url', 'password'];

const CartMutations = {
  pending: 0,

  _chain: Promise.resolve(),
  _seq: 0,

  // Sequence is claimed here, not in _execute, so a mutation queued behind an
  // in-flight one already invalidates that one's repaint.
  run(options) {
    const seq = ++this._seq;
    this.pending++;
    this._dispatch('cart:mutation-start');
    this._chain = this._chain.then(() => this._execute(options, seq));
    return this._chain;
  },

  // The staleness rule, named once. Callers get their `seq` back on the settled
  // result so a line-local rollback can honour the same gate the morph does —
  // when a newer mutation is queued its repaint restores the line from truth,
  // so an older failure must leave the DOM alone.
  isLatest(seq) {
    return seq === this._seq;
  },

  // One gate for both hosts, which ran identical copies of it. A success spends the
  // mutation's slice; a latest failure rolls it back and restores the line, unless a
  // newer prediction is still sitting in the coalescing window and would be clobbered.
  settleLine(result, { key, root, pending = null }) {
    if (result?.ok) window.CartOptimistic?.settle(key);
    else if (this.isLatest(result?.seq) && !pending?.has(key)) window.CartOptimistic?.revert(key, root);

    return result;
  },

  async _execute(options, seq) {
    const {
      requestFn,
      root,
      sectionId,
      morphTargets = [],
      scrollTarget = null,
      control = null,
      lineKey = null,
      announce = null,
      busy = true,
    } = options;

    // Opt-out, not the default: a control whose result is already painted locally —
    // a quantity stepper — must stay live and legible, or the prediction layer's
    // whole point is undone by a spinner over the button being clicked. `control` is
    // still carried, because the error slot is resolved from it. Scoped to the
    // control alone: no mutation marks the checkout button busy, only a click on it.
    const showBusy = busy && Boolean(control);

    if (showBusy) {
      control.disabled = true;
      control.classList.add('cart-is-busy');
    }

    // Carried on `cart:mutation-end` so listeners can tell a repaint from a failure —
    // a failure never morphs, so nothing else restores state they changed optimistically.
    let ok = false;

    try {
      const data = await requestFn(sectionId ? [sectionId] : []);
      const isLatest = this.isLatest(seq);

      if (isLatest) this.clearErrors(root);

      if (isLatest && sectionId && data.sections?.[sectionId]) {
        const morphed = this._morph(root, data.sections[sectionId], morphTargets, scrollTarget);
        if (!morphed) {
          this.showError(root, { message: root?.dataset.morphError ?? null });
          return { ok: false, seq };
        }
      }

      if (data.item_count !== undefined) window.CartStore?.sync(data);

      if (isLatest) {
        this._repaintChrome(root, data);
        // After the morph, so the repaint cannot wipe the announcement.
        this.announce(root, typeof announce === 'function' ? announce(data) : announce);
      }

      ok = true;
      return { ok: true, data, seq };
    } catch (error) {
      if (this.isLatest(seq)) this.showError(root, { control, lineKey, error });
      return { ok: false, error, seq };
    } finally {
      if (showBusy) {
        control.disabled = false;
        control.classList.remove('cart-is-busy');
      }
      this.pending = Math.max(0, this.pending - 1);
      this._dispatch('cart:mutation-end', { ok });
    }
  },

  // Blanked first, then set on the next frame. Assistive tech diffs live-region
  // content and stays silent when consecutive announcements are identical, so each
  // one has to land as a distinct change rather than an idempotent write — adding
  // the same product twice must be spoken twice.
  announce(root, message) {
    if (!root || !message) return;
    const region = root.querySelector('[data-cart-announce]');
    if (!region) return;

    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  },

  showError(root, { control = null, lineKey = null, scope = null, error = null, message = null } = {}) {
    if (!root) return;
    const slot = this._resolveSlot(root, { control, lineKey, scope });
    if (!slot) return;

    this.clearErrors(root);
    slot.textContent = message ?? error?.description ?? error?.message ?? slot.dataset.errorMessage ?? '';
    slot.hidden = false;
  },

  clearErrors(root) {
    if (!root) return;
    for (const slot of root.querySelectorAll('[data-cart-error-slot]')) {
      slot.hidden = true;
      slot.textContent = '';
    }
  },

  // The scope identity check matters: a line row's scope contains the size
  // switcher's nested scope, so the first slot found inside is not always ours.
  _resolveSlot(root, { control, lineKey, scope = null }) {
    // A named slot wins outright: it is the only form that survives a morph having
    // detached `control`, which is exactly what the element chain below cannot handle.
    if (scope) {
      const named = root.querySelector(`[data-cart-error-slot="${scope}"]`);
      if (named) return named;
    }

    const ownerScope = control?.isConnected ? control.closest('[data-error-scope]') : null;

    if (ownerScope) {
      const owned = [...ownerScope.querySelectorAll('[data-cart-error-slot]')].find(
        (slot) => slot.closest('[data-error-scope]') === ownerScope,
      );
      if (owned) return owned;
    }

    if (lineKey) {
      const lineSlot = root.querySelector(`[data-item-key="${lineKey}"] [data-cart-error-slot="line"]`);
      if (lineSlot) return lineSlot;
    }

    return root.querySelector('[data-cart-error-slot="global"]');
  },

  _dispatch(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail: { pending: this.pending, ...detail } }));
  },

  _morph(root, sectionHtml, morphTargets, scrollTarget) {
    if (typeof Idiomorph === 'undefined') {
      console.error('[CartMutations] Idiomorph unavailable — skipping repaint.');
      return false;
    }

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const scrollTop = scrollTarget ? (root.querySelector(scrollTarget)?.scrollTop ?? 0) : 0;
    const focusToken = this._captureFocus(root);

    this._sweepCarousels(root, morphTargets, 'destroyCarousel');

    for (const selector of morphTargets) {
      const existing = root.querySelector(selector);
      const incoming = doc.querySelector(selector);
      if (existing && incoming) Idiomorph.morph(existing, incoming, { morphStyle: 'outerHTML' });
    }

    if (scrollTarget) {
      const restored = root.querySelector(scrollTarget);
      if (restored) restored.scrollTop = scrollTop;
    }

    this._restoreFocus(root, focusToken);
    this._sweepCarousels(root, morphTargets, 'reInit');
    return true;
  },

  _captureFocus(root) {
    const active = document.activeElement;
    if (!active || !root.contains(active)) return null;

    // `data-focus-key` is checked first: it is an author-declared identity chosen to
    // be stable across the morph, whereas an `id` may be derived from the very value
    // the mutation changes (the size switcher's id is built from the line key).
    if (active.dataset.focusKey) return { type: 'key', value: active.dataset.focusKey };
    if (active.id) return { type: 'id', value: active.id };

    const row = active.closest('[data-item-key]');
    const attr = FOCUS_ATTRIBUTES.find((name) => active.hasAttribute(name));
    if (!row || !attr) return null;

    return { type: 'line', key: row.dataset.itemKey, variant: row.dataset.lineVariantId, attr };
  },

  _restoreFocus(root, token) {
    if (!token) return;

    let target = null;
    if (token.type === 'id') {
      target = root.querySelector(`#${CSS.escape(token.value)}`);
    } else if (token.type === 'key') {
      target = root.querySelector(`[data-focus-key="${token.value}"]`);
    } else {
      // A discount split can re-key the row on the same variant, so the variant is the fallback.
      const row =
        root.querySelector(`[data-item-key="${CSS.escape(token.key)}"]`) ??
        (token.variant ? root.querySelector(`[data-line-variant-id="${CSS.escape(token.variant)}"]`) : null);
      target = row?.querySelector(`[${token.attr}]`) ?? null;
    }

    if (!target) return;

    target.focus({ preventScroll: true });

    // A restored draft is almost always mid-correction, so the caret belongs after it.
    // The type check is not defensive padding: reading `selectionStart` on a
    // `type="number"` input throws outright in Chromium.
    const isTextarea = target.tagName === 'TEXTAREA';
    const isTextInput = target.tagName === 'INPUT' && SELECTABLE_INPUT_TYPES.includes(target.type);
    if (!isTextarea && !isTextInput) return;

    const caret = target.value.length;
    target.setSelectionRange(caret, caret);
  },

  _sweepCarousels(root, morphTargets, method) {
    for (const selector of morphTargets) {
      const region = root.querySelector(selector);
      if (!region) continue;
      for (const track of region.querySelectorAll('carousel-track')) track[method]?.();
    }
  },

  // add.js returns the added line, not the cart, so fall back to the count the
  // freshly morphed header carries.
  _repaintChrome(root, data) {
    let count = data.item_count;

    if (count === undefined) {
      const parsed = parseInt(root.querySelector('[data-cart-item-count]')?.dataset.cartItemCount ?? '', 10);
      if (Number.isNaN(parsed)) return;
      count = parsed;
    }

    // The gift-excluding figure is only ever computed in Liquid, so it comes off the
    // morphed header even when the response carried a count of its own. Absent on a
    // surface that does not render that header — the cart page — which then leaves
    // the drawer's badge to whichever morph next repaints it.
    const displayEl = root.querySelector(window.CartStore?.DISPLAY_COUNT_SELECTOR ?? '[data-cart-display-count]');
    const parsedDisplay = parseInt(displayEl?.dataset.cartDisplayCount ?? '', 10);

    window.CartStore?.reconcile(count, Number.isNaN(parsedDisplay) ? null : parsedDisplay);
  },
};

window.CartMutations = CartMutations;
